import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { 
  Key, Plus, Copy, Eye, EyeOff, Clock, CheckCircle2, XCircle, 
  AlertTriangle, Trash2, BarChart3, BookOpen, Zap, Shield, ExternalLink
} from "lucide-react";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { PublicApiCredential } from "@shared/schema";

interface ApiCredentialWithUsage extends PublicApiCredential {
  accountName?: string;
}

const statusStyles: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof CheckCircle2 }> = {
  pending: { variant: "secondary", icon: Clock },
  approved: { variant: "default", icon: CheckCircle2 },
  rejected: { variant: "destructive", icon: XCircle },
  suspended: { variant: "outline", icon: AlertTriangle },
  revoked: { variant: "destructive", icon: Trash2 },
};

export default function ApiAccessPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyDescription, setNewKeyDescription] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [deleteCredentialId, setDeleteCredentialId] = useState<string | null>(null);

  const { data: credentials, isLoading } = useQuery<ApiCredentialWithUsage[]>({
    queryKey: ["/api/my-api-credentials"],
    enabled: !!user, // Only fetch when authenticated
  });

  const createCredentialMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const response = await apiRequest("POST", "/api/my-api-credentials", data);
      return response as { credential: ApiCredentialWithUsage; apiKey: string };
    },
    onSuccess: (data) => {
      setNewlyCreatedKey(data.apiKey);
      queryClient.invalidateQueries({ queryKey: ["/api/my-api-credentials"] });
      toast({
        title: "API Key Requested",
        description: "Your API key request has been submitted for approval.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCredentialMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/my-api-credentials/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my-api-credentials"] });
      toast({
        title: "API Key Deleted",
        description: "The API key has been deleted.",
      });
      setDeleteCredentialId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCreateCredential = () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your API key.",
        variant: "destructive",
      });
      return;
    }
    createCredentialMutation.mutate({ name: newKeyName, description: newKeyDescription });
  };

  const handleCopyKey = () => {
    if (newlyCreatedKey) {
      navigator.clipboard.writeText(newlyCreatedKey);
      toast({
        title: "Copied",
        description: "API key copied to clipboard.",
      });
    }
  };

  const handleCloseNewKeyDialog = () => {
    setShowNewKeyDialog(false);
    setNewKeyName("");
    setNewKeyDescription("");
    setNewlyCreatedKey(null);
    setShowKey(false);
  };

  const getUsagePercentage = (cred: ApiCredentialWithUsage) => {
    return Math.min((cred.currentMonthInventoryUsage / cred.monthlyInventoryLimit) * 100, 100);
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-8 text-center">
            <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Sign in to manage API keys</h2>
            <p className="text-muted-foreground mb-4">
              You need to be logged in to request and manage your API credentials.
            </p>
            <div className="flex gap-3 justify-center">
              <Link href="/login?redirect=/api-access" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2" data-testid="btn-login">
                Log In
              </Link>
              <Link href="/api-docs" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2" data-testid="btn-view-docs">
                <BookOpen className="h-4 w-4 mr-2" />
                View Documentation
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Access</h1>
          <p className="text-muted-foreground mt-2">
            Manage your API credentials to integrate with the OpenBox platform.
          </p>
        </div>
        <Link href="/api-docs" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2" data-testid="link-api-docs">
          <BookOpen className="h-4 w-4 mr-2" />
          View Documentation
        </Link>
      </div>

      {/* Rate Limits Info */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5" />
            Rate Limits & Quotas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="font-semibold">Inventory Endpoints</div>
                <p className="text-sm text-muted-foreground">1,000 requests per month</p>
                <p className="text-xs text-muted-foreground mt-1">Products, inventory browsing</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="font-semibold">Order Endpoints</div>
                <p className="text-sm text-muted-foreground">Unlimited requests</p>
                <p className="text-xs text-muted-foreground mt-1">Create orders, check status</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Your API Keys</CardTitle>
            <CardDescription>
              Create and manage API keys for accessing the OpenBox API.
            </CardDescription>
          </div>
          <Dialog open={showNewKeyDialog} onOpenChange={setShowNewKeyDialog}>
            <DialogTrigger asChild>
              <Button data-testid="button-request-api-key">
                <Plus className="h-4 w-4 mr-2" />
                Request API Key
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              {!newlyCreatedKey ? (
                <>
                  <DialogHeader>
                    <DialogTitle>Request New API Key</DialogTitle>
                    <DialogDescription>
                      Create a new API key for your integrations. API keys require approval before they become active.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="keyName">Key Name *</Label>
                      <Input
                        id="keyName"
                        placeholder="e.g., Production Integration"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        data-testid="input-api-key-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="keyDescription">Description (Optional)</Label>
                      <Textarea
                        id="keyDescription"
                        placeholder="What will this key be used for?"
                        value={newKeyDescription}
                        onChange={(e) => setNewKeyDescription(e.target.value)}
                        data-testid="input-api-key-description"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={handleCloseNewKeyDialog}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateCredential}
                      disabled={createCredentialMutation.isPending}
                      data-testid="button-submit-api-key"
                    >
                      {createCredentialMutation.isPending ? "Submitting..." : "Submit Request"}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle>API Key Created</DialogTitle>
                    <DialogDescription>
                      Your API key has been created and is pending approval. Save it now - you won't be able to see it again.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <code className="flex-1 text-sm font-mono break-all">
                        {showKey ? newlyCreatedKey : "•".repeat(32)}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowKey(!showKey)}
                        data-testid="button-toggle-key-visibility"
                      >
                        {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleCopyKey}
                        data-testid="button-copy-key"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      This key will only be shown once. Copy it now!
                    </p>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCloseNewKeyDialog} data-testid="button-done">
                      Done
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading...
            </div>
          ) : !credentials?.length ? (
            <div className="py-8 text-center">
              <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-medium mb-2">No API keys yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Request an API key to start integrating with the OpenBox platform.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setShowNewKeyDialog(true)}
                data-testid="button-request-first-key"
              >
                <Plus className="h-4 w-4 mr-2" />
                Request Your First API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {credentials.map((cred) => {
                const StatusIcon = statusStyles[cred.status]?.icon || Clock;
                return (
                  <div 
                    key={cred.id} 
                    className="border rounded-lg p-4 space-y-4"
                    data-testid={`api-key-${cred.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          cred.status === 'approved' 
                            ? 'bg-green-100 dark:bg-green-900/30' 
                            : cred.status === 'pending'
                            ? 'bg-amber-100 dark:bg-amber-900/30'
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          <Key className={`h-5 w-5 ${
                            cred.status === 'approved' 
                              ? 'text-green-600 dark:text-green-400' 
                              : cred.status === 'pending'
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-red-600 dark:text-red-400'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{cred.name}</h4>
                            <Badge variant={statusStyles[cred.status]?.variant || "secondary"}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {cred.status.charAt(0).toUpperCase() + cred.status.slice(1)}
                            </Badge>
                          </div>
                          {cred.description && (
                            <p className="text-sm text-muted-foreground mt-1">{cred.description}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Created {formatDate(cred.createdAt)}
                          </p>
                        </div>
                      </div>
                      {cred.status !== "approved" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteCredentialId(cred.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`button-delete-api-key-${cred.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {cred.status === "approved" && (
                      <>
                        <Separator />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <div className="text-sm font-medium mb-1">Monthly Usage</div>
                            <div className="flex items-center gap-2">
                              <Progress value={getUsagePercentage(cred)} className="flex-1" />
                              <span className="text-sm text-muted-foreground whitespace-nowrap">
                                {cred.currentMonthInventoryUsage.toLocaleString()} / {cred.monthlyInventoryLimit.toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Resets {formatDate(cred.usageResetAt)}
                            </p>
                          </div>
                          <div>
                            <div className="text-sm font-medium mb-1">Total Requests</div>
                            <p className="text-2xl font-bold">{cred.totalRequests.toLocaleString()}</p>
                          </div>
                          <div>
                            <div className="text-sm font-medium mb-1">Last Used</div>
                            <p className="text-sm text-muted-foreground">{formatDate(cred.lastUsedAt)}</p>
                          </div>
                        </div>
                      </>
                    )}

                    {cred.status === "rejected" && cred.rejectionReason && (
                      <>
                        <Separator />
                        <div className="bg-destructive/10 rounded p-3">
                          <div className="text-sm font-medium text-destructive mb-1">Rejection Reason</div>
                          <p className="text-sm">{cred.rejectionReason}</p>
                        </div>
                      </>
                    )}

                    {cred.status === "pending" && (
                      <>
                        <Separator />
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-3">
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            Your API key request is pending review. You'll receive an email once it's approved.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link href="/api-docs" className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted transition-colors">
              <BookOpen className="h-5 w-5 text-primary" />
              <div>
                <div className="font-medium">API Documentation</div>
                <p className="text-sm text-muted-foreground">Code examples and endpoint reference</p>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-muted-foreground" />
            </Link>
            <a 
              href="mailto:jimmy@shopjimmy.com"
              className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted transition-colors"
            >
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <div className="font-medium">Need Help?</div>
                <p className="text-sm text-muted-foreground">Contact support for integration help</p>
              </div>
              <ExternalLink className="h-4 w-4 ml-auto text-muted-foreground" />
            </a>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteCredentialId} onOpenChange={() => setDeleteCredentialId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The API key will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCredentialId && deleteCredentialMutation.mutate(deleteCredentialId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
